import { Box as MuiBox, SxProps, Theme, useTheme } from '@mui/material';
import type { ElementType } from 'react';

import type { BoxLayoutProps } from './boxLayout.types';

export type { BoxLayoutProps } from './boxLayout.types';

interface BoxProps extends BoxLayoutProps {
  children: React.ReactNode;
  className?: string;
  component?: ElementType;
  variant?: 'default' | 'shadow';
}

/**
 * Maps {@link BoxLayoutProps} into MUI `sx` fragments for internal composition.
 * @param layout - Optional layout fields from {@link Box}.
 * @returns A partial `sx` object with only defined keys.
 */
function buildLayoutSx(layout: BoxLayoutProps): SxProps<Theme> {
  const sx: Record<string, unknown> = {};

  if (layout.display !== undefined) sx.display = layout.display;
  if (layout.flexDirection !== undefined) sx.flexDirection = layout.flexDirection;
  if (layout.flex !== undefined) sx.flex = layout.flex;
  if (layout.flexGrow !== undefined) sx.flexGrow = layout.flexGrow;
  if (layout.flexShrink !== undefined) sx.flexShrink = layout.flexShrink;
  if (layout.fontSize !== undefined) sx.fontSize = layout.fontSize;
  if (layout.gap !== undefined) sx.gap = layout.gap;
  if (layout.alignItems !== undefined) sx.alignItems = layout.alignItems;
  if (layout.alignSelf !== undefined) sx.alignSelf = layout.alignSelf;
  if (layout.justifyContent !== undefined) {
    sx.justifyContent = layout.justifyContent;
  }
  if (layout.height !== undefined) sx.height = layout.height;
  if (layout.minHeight !== undefined) sx.minHeight = layout.minHeight;
  if (layout.minWidth !== undefined) sx.minWidth = layout.minWidth;
  if (layout.maxWidth !== undefined) sx.maxWidth = layout.maxWidth;
  if (layout.width !== undefined) sx.width = layout.width;
  if (layout.widthMaxContent) sx.width = 'max-content';
  if (layout.position !== undefined) sx.position = layout.position;
  if (layout.top !== undefined) sx.top = layout.top;
  if (layout.right !== undefined) sx.right = layout.right;
  if (layout.bottom !== undefined) sx.bottom = layout.bottom;
  if (layout.left !== undefined) sx.left = layout.left;
  if (layout.transform !== undefined) sx.transform = layout.transform;
  if (layout.padding !== undefined) sx.padding = layout.padding;
  if (layout.paddingRight !== undefined) sx.paddingRight = layout.paddingRight;
  if (layout.margin !== undefined) sx.margin = layout.margin;
  if (layout.marginTop !== undefined) sx.marginTop = layout.marginTop;
  if (layout.marginBottom !== undefined) sx.marginBottom = layout.marginBottom;
  if (layout.marginLeft !== undefined) sx.marginLeft = layout.marginLeft;
  if (layout.overflow !== undefined) sx.overflow = layout.overflow;
  if (layout.overflowX !== undefined) sx.overflowX = layout.overflowX;
  if (layout.overflowY !== undefined) sx.overflowY = layout.overflowY;
  if (layout.boxSizing !== undefined) sx.boxSizing = layout.boxSizing;
  if (layout.backgroundColor !== undefined) {
    sx.backgroundColor = layout.backgroundColor;
  }
  if (layout.background !== undefined) sx.background = layout.background;
  if (layout.border !== undefined) sx.border = layout.border;
  if (layout.borderRadius !== undefined) sx.borderRadius = layout.borderRadius;
  if (layout.borderBottom !== undefined) sx.borderBottom = layout.borderBottom;
  if (layout.borderTop !== undefined) sx.borderTop = layout.borderTop;
  if (layout.boxShadow !== undefined) sx.boxShadow = layout.boxShadow;
  if (layout.backdropFilter !== undefined) {
    sx.backdropFilter = layout.backdropFilter;
  }
  if (layout.zIndex !== undefined) sx.zIndex = layout.zIndex;
  if (layout.textAlign !== undefined) sx.textAlign = layout.textAlign;
  if (layout.pointerEvents !== undefined) {
    sx.pointerEvents = layout.pointerEvents;
  }
  if (layout.lineHeight !== undefined) sx.lineHeight = layout.lineHeight;
  if (layout.overflowWrap !== undefined) sx.overflowWrap = layout.overflowWrap;
  if (layout.textOverflow !== undefined) sx.textOverflow = layout.textOverflow;
  if (layout.whiteSpace !== undefined) sx.whiteSpace = layout.whiteSpace;
  if (layout.wordBreak !== undefined) sx.wordBreak = layout.wordBreak;
  if (layout.wordWrap !== undefined) sx.wordWrap = layout.wordWrap;

  return sx as SxProps<Theme>;
}

/**
 * Design-system layout wrapper around MUI `Box`. Prefer these props over raw `sx` in feature code.
 * @param props - Visual variant, optional root `component`, and {@link BoxLayoutProps}.
 * @returns The rendered MUI `Box`.
 */
export const Box = ({
  children,
  className,
  component,
  variant = 'default',
  ...layout
}: BoxProps) => {
  const theme = useTheme();

  const variantSx: SxProps<Theme> =
    variant === 'shadow'
      ? {
          backgroundColor: '#EFF1F2',
          borderRadius: theme.app.borderRadius.lessRounded,
          padding: 2,
          margin: '16px 0',
          width: '100%',
          boxSizing: 'border-box',
        }
      : {};

  return (
    <MuiBox
      className={className}
      component={component}
      sx={{ ...variantSx, ...buildLayoutSx(layout) }}
    >
      {children}
    </MuiBox>
  );
};
